// Public review page for an emailed invitation. No login: the token in the link is the only credential.
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';

export default function ReviewPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [supplierName, setSupplierName] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | invalid | done
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    api.get(`/api/review-invitations/${encodeURIComponent(token)}`)
      .then((res) => { setSupplierName(res.data.supplierName); setStatus('ready'); })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating || saving) return;
    setSaving(true);
    setMessage('');
    try {
      await api.post(`/api/review-invitations/${encodeURIComponent(token)}/review`, { rating, comment });
      setStatus('done');
    } catch (err) {
      if (err.response?.status === 409) setStatus('done'); // already reviewed: the same outcome
      else setMessage(err.response?.data?.message || 'Could not save your review. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (status === 'loading') return <div className="max-w-md mx-auto p-6">Loading…</div>;
  if (status === 'invalid') return <div className="max-w-md mx-auto p-6"><h1 className="text-xl font-semibold">This link is no longer valid</h1><p className="text-gray-600 mt-2">It may have expired or already been used.</p></div>;
  if (status === 'done') return <div className="max-w-md mx-auto p-6"><h1 className="text-xl font-semibold">Thank you for your review</h1><p className="text-gray-600 mt-2">It will appear on the supplier's profile after review.</p></div>;

  return (
    <form onSubmit={submit} className="max-w-md mx-auto p-6 space-y-4">
      <h1 className="text-xl font-semibold break-words">Review {supplierName}</h1>
      <fieldset>
        <legend className="text-sm font-medium mb-1">Rating</legend>
        <div role="radiogroup" aria-label="Rating" className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <label key={n} className={`text-3xl leading-none cursor-pointer rounded px-0.5 focus-within:ring-2 focus-within:ring-blue-500 ${n <= rating ? 'text-amber-500' : 'text-gray-300'}`}>
              <input type="radio" name="rating" value={n} checked={rating === n} onChange={() => setRating(n)} aria-label={`${n} star${n === 1 ? '' : 's'}`} className="sr-only" />
              ★
            </label>
          ))}
        </div>
      </fieldset>
      <label className="block text-sm font-medium text-gray-700">
        Comment (optional)
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} rows={3} className="mt-1 w-full border rounded px-2 py-1 font-normal" />
      </label>
      {message && <p role="alert" className="text-red-600 text-sm">{message}</p>}
      <button type="submit" disabled={!rating || saving} className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50">
        {saving ? 'Submitting…' : 'Submit review'}
      </button>
    </form>
  );
}
